import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { usersApi, tenantsApi } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { useConfirm } from '../components/common/ConfirmDialog'

interface User {
  id: string
  username: string
  email: string
  role: string
  is_active: boolean
  tenant_id: string | null
  tenant?: { name: string }
}

interface Tenant {
  id: string
  name: string
}

const emptyForm = {
  username: '',
  email: '',
  password: '',
  role: 'user',
  tenant_id: '',
  is_active: true
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const confirm = useConfirm()
  const [formData, setFormData] = useState(emptyForm)
  const currentUser = useAuthStore(state => state.user)
  const isSuperAdmin = currentUser?.role === 'super_admin'

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [usersRes, tenantsRes] = await Promise.all([
        usersApi.list(),
        isSuperAdmin ? tenantsApi.list() : Promise.resolve({ data: { items: [] } })
      ])
      setUsers(usersRes.data.items || [])
      setTenants(tenantsRes.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingUser(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (user: User) => {
    setEditingUser(user)
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      tenant_id: user.tenant_id || '',
      is_active: user.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData: Record<string, unknown> = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active
      }

      // Only include password if provided
      if (formData.password) {
        submitData.password = formData.password
      }

      // Include tenant_id for new users or if super admin
      if (!editingUser && formData.tenant_id) {
        submitData.tenant_id = formData.tenant_id
      }

      if (editingUser) {
        await usersApi.update(editingUser.id, submitData)
        toast.success('Benutzer aktualisiert')
      } else {
        await usersApi.create(submitData)
        toast.success('Benutzer erstellt')
      }
      closeModal()
      loadData()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || (editingUser ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen'))
    }
  }

  const handleDelete = async (user: User) => {
    if (user.id === currentUser?.id) {
      toast.error('Sie können sich nicht selbst löschen')
      return
    }
    const confirmed = await confirm({
      title: 'Benutzer löschen',
      message: `Benutzer "${user.username}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await usersApi.delete(user.id)
      toast.success('Benutzer gelöscht')
      loadData()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingUser(null)
    setFormData(emptyForm)
  }

  const getRoleBadge = (role: string) => {
    if (role === 'super_admin') return <Badge bg="danger">Super Admin</Badge>
    if (role === 'tenant_admin') return <Badge bg="warning" text="dark">Mandanten-Admin</Badge>
    return <Badge bg="secondary">Benutzer</Badge>
  }

  const getTenantName = (user: User) => {
    if (user.role === 'super_admin') return <span className="text-muted">-</span>
    if (user.tenant) return user.tenant.name
    const tenant = tenants.find(t => t.id === user.tenant_id)
    return tenant?.name || <span className="text-muted">Kein Mandant</span>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Benutzer</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neuer Benutzer
        </Button>
      </div>
      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Benutzername</th>
              <th>E-Mail</th>
              {isSuperAdmin && <th>Mandant</th>}
              <th>Rolle</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuperAdmin ? 6 : 5} className="text-center py-4">Laden...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? 6 : 5} className="text-center py-4 text-muted">Keine Benutzer</td></tr>
            ) : (
              users.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.username}</strong></td>
                  <td>{u.email}</td>
                  {isSuperAdmin && <td>{getTenantName(u)}</td>}
                  <td>{getRoleBadge(u.role)}</td>
                  <td>
                    <Badge bg={u.is_active ? 'success' : 'secondary'}>
                      {u.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => openEditModal(u)}
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(u)}
                      disabled={u.id === currentUser?.id}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {isSuperAdmin && !editingUser && (
              <Form.Group className="mb-3">
                <Form.Label>Mandant *</Form.Label>
                <Form.Select
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({...formData, tenant_id: e.target.value})}
                  required
                >
                  <option value="">-- Mandant wählen --</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Der Benutzer wird diesem Mandanten zugeordnet.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Benutzername *</Form.Label>
              <Form.Control
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                required
                placeholder="z.B. max.mustermann"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>E-Mail *</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="max@beispiel.de"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{editingUser ? 'Neues Passwort (leer lassen um zu behalten)' : 'Passwort *'}</Form.Label>
              <Form.Control
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required={!editingUser}
                minLength={8}
                placeholder="Mindestens 8 Zeichen"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Rolle</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
              >
                <option value="user">Benutzer</option>
                <option value="tenant_admin">Mandanten-Admin</option>
                {isSuperAdmin && <option value="super_admin">Super Admin</option>}
              </Form.Select>
            </Form.Group>

            {editingUser && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="is_active"
                  label="Aktiv"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingUser ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
